import { memo, useState, useEffect } from "react";
import {
  Box,
  Text,
  Input,
  useColorMode,
  Flex,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from "@chakra-ui/react";
import { getCurrentColor } from "../../constants";
import {
  DangerousIcon,
  MediumIcon,
  VettedIcon,
} from "../../assets/icons/icons";
import Filter from "./Filter";
import ConditionFilter from "./ConditionFilter";
import { useLocation, useSearchParams } from "react-router-dom";
import { useAtom } from "jotai";
import {
  categoriesPageModeAtom,
  filterInfoAtom,
  productsLoadingStatusAtom as listingsLoadingStatusAtom,
} from "../../atom/listings";
import {
  filterInfoAtom as productsFiltersInfoAtom,
  productsLoadingStatusAtom,
} from "../../atom/products";
import useFilter from "../../hooks/useFilter";

const FiltersWrapper = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [categoriesPageMode] = useAtom(categoriesPageModeAtom);
  const { UpdateKeywordFilter } = useFilter();

  const isProductsPageMode = categoriesPageMode === "products";

  const [filterInfo] = useAtom(
    isProductsPageMode ? productsFiltersInfoAtom : filterInfoAtom,
  );
  const [filtersLoadingStatus] = useAtom(
    isProductsPageMode ? productsLoadingStatusAtom : listingsLoadingStatusAtom,
  );

  const shipping = [
    { name: "Verified", icon: <VettedIcon />, id: "1" },
    { name: "Caution", icon: <MediumIcon />, id: "2" },
    { name: "Unknown", icon: <DangerousIcon />, id: "3" },
  ];

  const createShipFlag = () =>
    filterInfo["ship_flag"]
      .map((item) => ({
        ...item,
        name: shipping[+item.name - 1].name,
        id: item.name,
        flag: item.name,
        icon: shipping[+item.name - 1].icon,
      }))
      .sort((a, b) => +b.id - +a.id);

  // State for keyword filters
  const [includeKeywords, setIncludeKeywords] = useState("");
  const [excludeKeywords, setExcludeKeywords] = useState("");
  const { colorMode } = useColorMode();

  // Initialize keyword filters from URL params
  useEffect(() => {
    const sIn = searchParams.get("s_in") || "";
    const sOut = searchParams.get("s_out") || "";
    setIncludeKeywords(sIn);
    setExcludeKeywords(sOut);
  }, [searchParams]);

  // Handle keyword input changes and update filter state
  const handleIncludeChange = (e) => {
    const value = e.target.value;
    setIncludeKeywords(value);
    UpdateKeywordFilter("s_in", value);
  };

  const handleExcludeChange = (e) => {
    const value = e.target.value;
    setExcludeKeywords(value);
    UpdateKeywordFilter("s_out", value);
  };

  // Title Keywords filter UI matching other filter boxes
  const renderTitleKeywordsFilter = () => {
    const borderCol = getCurrentColor(colorMode, "grayC.500", "grayC.2400");

    return (
      <Flex w={"100%"}>
        <Accordion allowMultiple w={"100%"} defaultIndex={[0]}>
          <AccordionItem
            w={"100%"}
            borderRadius={{ base: "0px", lg: "6px" }}
            border={{ base: "none", lg: "1px solid" }}
            height={"100%"}
            borderColor={getCurrentColor(
              colorMode,
              "grayC.500 !important",
              "transparent !important",
            )}
            _hover={{
              bg: getCurrentColor(colorMode, "grayC.600", "grayC.1700"),
            }}
          >
            <AccordionButton
              bg={getCurrentColor(colorMode, "grayC.600", "grayC.1700")}
              borderTopRadius={{ base: "0px", lg: "6px" }}
            >
              <Box
                as="span"
                flex="1"
                color={getCurrentColor(colorMode, "black.200", "grayC.1600")}
                textAlign="left"
              >
                Title Keywords
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel
              pt={{ base: "21px", md: "16px" }}
              pb={4}
              pr={3}
              w={"100%"}
              bg={getCurrentColor(colorMode, "whiteC.100", "grayC.1900")}
              borderBottomRadius={{ base: "0px", lg: "6px" }}
            >
              <Box mb={"18px"}>
                <Text
                  fontWeight={600}
                  color={getCurrentColor(colorMode, "blackC.200", "grayC.1600")}
                  fontSize="sm"
                  mb={2}
                >
                  Include Keywords:
                </Text>
                <Input
                  value={includeKeywords}
                  onChange={handleIncludeChange}
                  placeholder="Search"
                  w={"100%"}
                  pl={3}
                  py={"11px"}
                  pr={3}
                  bg={getCurrentColor(colorMode, "grayC.600", "grayC.2000")}
                  color={getCurrentColor(colorMode, "grayC.1900", "grayC.1600")}
                  border={"1px solid"}
                  borderColor={borderCol}
                  outline={"none"}
                  focusBorderColor={getCurrentColor(
                    colorMode,
                    "midnightC.400",
                    "none",
                  )}
                />
              </Box>

              <Box>
                <Text
                  fontWeight={600}
                  color={getCurrentColor(colorMode, "blackC.200", "grayC.1600")}
                  fontSize="sm"
                  mb={2}
                >
                  Exclude Keywords:
                </Text>
                <Input
                  value={excludeKeywords}
                  onChange={handleExcludeChange}
                  placeholder="Search"
                  w={"100%"}
                  pl={3}
                  py={"11px"}
                  pr={3}
                  bg={getCurrentColor(colorMode, "grayC.600", "grayC.2000")}
                  color={getCurrentColor(colorMode, "grayC.1900", "grayC.1600")}
                  border={"1px solid"}
                  borderColor={borderCol}
                  outline={"none"}
                  focusBorderColor={getCurrentColor(
                    colorMode,
                    "midnightC.400",
                    "none",
                  )}
                />
              </Box>
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      </Flex>
    );
  };

  return (
    filterInfo !== null &&
    filtersLoadingStatus === "idle" && (
      <>
        {location.pathname.includes("Shotgun") ||
        location.pathname.includes("shotgun") ? (
          <>
            {renderTitleKeywordsFilter()}
            <Filter
              key={"bullet_type"}
              title={"Shot Type"}
              data={filterInfo["bullet_type"]}
              checkbox={true}
              search={true}
              query={"bt"}
              name={"Bullet Type"}
            />
            <Filter
              key={"bullet_type_s"}
              title={"Shot Size"}
              data={filterInfo["bullet_type_s"]}
              checkbox={true}
              search={true}
              query={"bullet_type_s"}
              name={"Shot Type"}
            />
            <Filter
              key={"bullet_weight"}
              title={"Shot Weight"}
              data={filterInfo["bullet_weight"]}
              checkbox={true}
              slider={true}
              query={"bw"}
              name={"Bullet Weight"}
            />
            <Filter
              key={"shell_length"}
              title={"Shell Length"}
              data={filterInfo["shell_length"]}
              checkbox={true}
              search={false}
              query={"shell_length"}
              name={"Shot Type"}
            />
            <Filter
              key={"material"}
              title={"Shot Material"}
              data={filterInfo["material"]}
              checkbox={true}
              search={false}
              query={"mt"}
              name={"Casing"}
            />
            <Filter
              key={"rdsperbox"}
              title={"Number of Rounds"}
              data={filterInfo["rdsperbox"]}
              checkbox={true}
              slider={true}
              query={"rpb"}
              name={"Rounds"}
            />
            <Filter
              key={"brand"}
              title={"Brand"}
              data={filterInfo["brand"]}
              checkbox={true}
              search={true}
              query={"brnd"}
              name={"Brand"}
            />
            {!isProductsPageMode && (
              <Filter
                key={"vendor_name"}
                title={"Vendor"}
                data={filterInfo["vendor_name"]}
                checkbox={true}
                search={true}
                query={"vid"}
                name={"Vendor"}
              />
            )}
            <Filter
              key={"ship_flag"}
              title={"Shipping"}
              data={createShipFlag()}
              checkbox={true}
              search={false}
              ship_flag={true}
              query={"sf"}
              name={"Shipping"}
            />
            {!isProductsPageMode && <ConditionFilter key={"condition"} />}
          </>
        ) : (
          <>
            {renderTitleKeywordsFilter()}
            <Filter
              key={"rdsperbox"}
              title={"Number of Rounds"}
              data={filterInfo["rdsperbox"]}
              checkbox={true}
              slider={true}
              query={"rpb"}
              name={"Rounds"}
            />
            <Filter
              key={"bullet_weight"}
              title={"Bullet Weight"}
              data={filterInfo["bullet_weight"]}
              checkbox={true}
              slider={true}
              query={"bw"}
              name={"Bullet Weight"}
            />
            <Filter
              key={"bullet_type_s"}
              title={"Bullet Type"}
              data={filterInfo["bullet_type_s"]}
              checkbox={true}
              search={true}
              query={"bullet_type_s"}
              name={"Bullet Type"}
            />
            <Filter
              key={"material"}
              title={"Material"}
              data={filterInfo["material"]}
              checkbox={true}
              search={false}
              query={"mt"}
              name={"Material"}
            />
            <Filter
              key={"brand"}
              title={"Brand"}
              data={filterInfo["brand"]}
              checkbox={true}
              search={true}
              query={"brnd"}
              name={"Brand"}
            />
            {!isProductsPageMode && (
              <Filter
                key={"vendor_name"}
                title={"Vendor"}
                data={filterInfo["vendor_name"]}
                checkbox={true}
                search={true}
                query={"vid"}
                name={"Vendor"}
              />
            )}
            <Filter
              key={"ship_flag"}
              title={"Shipping"}
              data={createShipFlag()}
              checkbox={true}
              search={false}
              ship_flag={true}
              query={"sf"}
              name={"Shipping"}
            />
            {!isProductsPageMode && <ConditionFilter key={"condition"} />}
          </>
        )}
      </>
    )
  );
};

export default memo(FiltersWrapper);
